import { epgProgramsModel, inRange, NO_EPG_ID } from '$/epg/programs'
import { pipe } from '@mobily/ts-belt'
import { combine, createEvent, createStore, sample, type Store } from 'effector'
import { model as session } from '~/entities/session'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { model as clock } from '~/shared/clock'
import { generateEmptyProgram, T } from '~/shared/helpers'
import { type LiveProgramLocalizedInfo } from './index.h'
import { formatToLocalizedViewWithLabels, formatToView } from './lib'

export const init = createEvent()

export const reset = createEvent()

// currently playing channel/program info
export const $playing: Store<LiveProgramLocalizedInfo | null> = combine(
  {
    channel: selectEpgChannelModel.$active,
    epg: epgProgramsModel.$epg,
    now: clock.$minutes,
  },
  ({ channel, epg: [programs], now }) => {
    if (channel == null) return null

    const epg = programs.get(channel.channelEpgId ?? NO_EPG_ID)

    const isLive = inRange(now)
    let program = epg?.find((p) => isLive([p.actualStartMs, p.actualStopMs]))
    if (program == null) {
      // you don't have to add a start|stop here, because the program time fields are not needed anywhere else in UI or calculations
      program = generateEmptyProgram(0, 0, NO_EPG_ID)
    }

    return pipe(
      { channel, program },
      formatToView,
      formatToLocalizedViewWithLabels()
    )
  }
)

// store that indicates if playing program info is ready to be displayed
// becomes true when epg is loaded and playing program is set and active channel is ready
export const $playingIsReady = createStore<boolean>(false).reset(
  session.$isAuthenticated
)

sample({
  clock: [
    epgProgramsModel.$epgIsReady,
    selectEpgChannelModel.$activeChannelIsReady,
  ],
  source: {
    epgIsReady: epgProgramsModel.$epgIsReady,
    activeChannelIsReady: selectEpgChannelModel.$activeChannelIsReady,
  },
  filter: ({ epgIsReady, activeChannelIsReady }) =>
    Boolean(epgIsReady && activeChannelIsReady),
  fn: T,
  target: $playingIsReady,
})

sample({
  clock: reset,
  target: [selectEpgChannelModel.$activeId.reinit],
})
